/** Format paise as `₹X,XXX.YY` using en-IN locale. */
export function format_inr(paise: number): string {
  const rupees = paise / 100;
  return `₹${rupees.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** Current calendar month as YYYY-MM. */
export function current_due_month(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Add `months` to a YYYY-MM string, returning a new YYYY-MM. */
export function offset_due_month(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// ─── IST-aware helpers (studio-side) ────────────────────────────────────────

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const FY_PATTERN = /^(\d{4})-(\d{2})$/;

/** YYYY-MM-DD in IST. */
export function today_ymd_ist(): string {
  const d = new Date(Date.now() + IST_OFFSET_MS);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** Add `n` days to a YYYY-MM-DD. */
export function offset_ymd(ymd: string, by_days: number): string {
  const m = YMD_PATTERN.exec(ymd);
  if (!m) return ymd;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) + by_days * 86_400_000;
  const d = new Date(t);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** "2025-26" for the FY containing the IST instant now. */
export function fiscal_year_label(now: Date = new Date()): string {
  const ist = new Date(now.getTime() + IST_OFFSET_MS);
  const y = ist.getUTCFullYear();
  const start = ist.getUTCMonth() >= 3 ? y : y - 1;
  const next_yy = String((start + 1) % 100).padStart(2, '0');
  return `${start}-${next_yy}`;
}

/** Resolve FY bounds (label optional; defaults to current FY in IST). */
export function fy_bounds(label?: string): { fy_label: string; start_ymd: string; end_ymd: string } {
  let start_year: number;
  if (label) {
    const m = FY_PATTERN.exec(label);
    start_year = m ? Number(m[1]) : fy_start_year_now();
  } else {
    start_year = fy_start_year_now();
  }
  const next_yy = String((start_year + 1) % 100).padStart(2, '0');
  return {
    fy_label: `${start_year}-${next_yy}`,
    start_ymd: `${start_year}-04-01`,
    end_ymd: `${start_year + 1}-03-31`,
  };
}

function fy_start_year_now(): number {
  const ist = new Date(Date.now() + IST_OFFSET_MS);
  const y = ist.getUTCFullYear();
  return ist.getUTCMonth() >= 3 ? y : y - 1;
}

/** Calendar-day diff between two YYYY-MM-DD strings (later − earlier). */
export function days_between(later_ymd: string, earlier_ymd: string): number {
  const la = YMD_PATTERN.exec(later_ymd);
  const ea = YMD_PATTERN.exec(earlier_ymd);
  if (!la || !ea) return 0;
  const lt = Date.UTC(Number(la[1]), Number(la[2]) - 1, Number(la[3]));
  const et = Date.UTC(Number(ea[1]), Number(ea[2]) - 1, Number(ea[3]));
  return Math.floor((lt - et) / 86_400_000);
}

const TA_MONTHS: Record<string, string> = {
  '01': 'ஜன', '02': 'பிப்', '03': 'மார்', '04': 'ஏப்', '05': 'மே', '06': 'ஜூன்',
  '07': 'ஜூ', '08': 'ஆக', '09': 'செப்', '10': 'அக்', '11': 'நவ', '12': 'டிச',
};
const EN_MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Short month name from YYYY-MM. Locale-aware. */
export function short_month_label(yyyy_mm: string, lang: string): string {
  const mm = yyyy_mm.slice(5);
  if (lang === 'ta' && TA_MONTHS[mm]) return TA_MONTHS[mm];
  return EN_MONTHS[Number(mm)] ?? mm;
}
